import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { NuqsAdapter } from "nuqs/adapters/react";
import { I18nextProvider } from "react-i18next";

import { routeTree } from "./routeTree.gen";

import "./styles/index.css";
import "./styles/markdown.css";
import i18n from "./lib/i18n";
import AuthenticationProvider, {
  useAuthentication,
} from "./contexts/AuthenticationProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { initSentry } from "./lib/sentry";
import { GlobalErrorBoundary } from "./components/error-boundary";

// Initialize Sentry only in production
if (import.meta.env.MODE === "production") {
  initSentry();
}

const queryClient = new QueryClient();

export const router = createRouter({
  routeTree,
  context: { authentication: null, queryClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const authentication = useAuthentication();
  return !authentication.isLoading ? (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ authentication }} />
    </QueryClientProvider>
  ) : null;
}

function App() {
  return (
    <AuthenticationProvider router={router}>
      <GlobalErrorBoundary>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
          storageKey="palman-theme"
        >
          <NuqsAdapter>
            <InnerApp />
          </NuqsAdapter>
        </ThemeProvider>
      </GlobalErrorBoundary>
    </AuthenticationProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <App />
    </I18nextProvider>
  </StrictMode>,
);
